import React, { useState, useEffect } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaRuler,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaCube,
  FaExpand,
  FaFilter
} from "react-icons/fa";

function SkuMeasurementsManager() {
  const [measurements, setMeasurements] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState(null);
  const [deletingMeasurement, setDeletingMeasurement] = useState(null);
  const [filterSkuId, setFilterSkuId] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    sku_id: '',
    measure_description: '',
    active: true
  });

  useEffect(() => {
    loadMeasurements();
    loadSkus();
  }, []);

  useEffect(() => {
    loadMeasurements();
  }, [filterSkuId]);

  const loadSkus = async () => {
    try {
      const response = await getData('/api/admin/get_sku_mains');
      let skusData = [];
      
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          skusData = response.data;
        } else if (response.data.skus && Array.isArray(response.data.skus)) {
          skusData = response.data.skus;
        } else {
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && skusData.length === 0) {
              skusData = response.data[key];
            }
          });
        }
      }
      
      setSkus(skusData);
    } catch (error) {
      console.error('Error cargando SKUs:', error);
    }
  };

  const loadMeasurements = async () => {
    try {
      setLoading(true);
      
      let endpoint = '/api/admin/get_sku_measurements';
      if (filterSkuId) {
        endpoint += `?sku_id=${filterSkuId}`;
      }
      
      const response = await getData(endpoint);
      
      // Inicializar como array vacío por defecto
      let measurementsData = [];
      
      if (response && response.data) {
        // La API retorna las medidas en response.data.skuMeasurements
        if (response.data.skuMeasurements && Array.isArray(response.data.skuMeasurements)) {
          measurementsData = response.data.skuMeasurements;
        }
        // Si response.data es directamente un array
        else if (Array.isArray(response.data)) {
          measurementsData = response.data;
        }
        // Si está en response.data.measurements
        else if (response.data.measurements && Array.isArray(response.data.measurements)) {
          measurementsData = response.data.measurements;
        }
        // Explorar otras propiedades posibles como fallback
        else {
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && measurementsData.length === 0) {
              measurementsData = response.data[key];
            }
          });
        }
      }
      
      
      // Asegurar que siempre sea un array
      if (!Array.isArray(measurementsData)) {
        measurementsData = [];
      }
      
      setMeasurements(measurementsData);
    } catch (error) {
      console.error('Error cargando medidas:', error);
      toastError('Error al cargar las medidas');
      setMeasurements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          sku_id: parseInt(formData.sku_id),
          measure_description: formData.measure_description.trim().toUpperCase(),
          active: formData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      if (editingMeasurement) {
        dataToSend.data.id = editingMeasurement.id;
        await putData('/api/admin/update_sku_measurement', dataToSend);
        toastSuccess(`Medida "${formData.measure_description}" actualizada exitosamente`);
      } else {
        await postData('/api/admin/create_sku_measurement', dataToSend);
        toastSuccess(`Medida "${formData.measure_description}" creada exitosamente`);
      }
      
      setShowModal(false);
      resetForm();
      await loadMeasurements();
    } catch (error) {
      console.error('Error guardando medida:', error);
      toastError(editingMeasurement ? 'Error al actualizar la medida' : 'Error al crear la medida');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingMeasurement(null);
    setFormData({
      sku_id: '',
      measure_description: '',
      active: true
    });
  };

  const handleEdit = (measurement) => {
    setEditingMeasurement(measurement);
    setFormData({
      sku_id: measurement.sku_id || '',
      measure_description: measurement.measure_description || '',
      active: measurement.active !== undefined ? Boolean(measurement.active) : true
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingMeasurement(null);
    setFormData({
      sku_id: '',
      measure_description: '',
      active: true
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (measurement) => {
    setDeletingMeasurement(measurement);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingMeasurement) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          id: deletingMeasurement.id
        }
      };

      const response = await deleteData('/api/admin/delete_sku_measurement', dataToSend);
      
      if (response?.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      toastSuccess(`Medida "${deletingMeasurement.measure_description}" eliminada exitosamente`);
      setShowDeleteModal(false);
      setDeletingMeasurement(null);
      await loadMeasurements();
    } catch (error) {
      console.error('Error eliminando medida:', error);
      toastError(error.message || 'Error al eliminar la medida');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingMeasurement(null);
  };

  // Medidas comunes basadas en la imagen proporcionada
  const commonMeasurements = [
    '100',
    '90X120',
    '100X120',
    '140X120',
    '145X120',
    '160X120',
    '180X120',
    '200X120',
    '200X130',
    '80X190X26',
    '100X190X26',
    '100X200X26',
    '180X200X26',
    '160X200X26',
    '200X200X26'
  ];

  // Función para obtener el nombre del SKU
  const getSkuName = (skuId) => {
    const sku = skus.find(s => s.id === skuId);
    return sku ? sku.sku_description || `SKU #${skuId}` : `SKU #${skuId}`;
  };

  // Función para formatear la descripción de medida con íconos
  const formatMeasureDescription = (description) => {
    // Contar las X para determinar si es 2D o 3D
    const xCount = (description.match(/X/g) || []).length;
    if (xCount >= 2) {
      return (
        <span>
          <FaCube className="me-2 text-info" />
          {description}
          <small className="text-muted ms-2">(3D)</small>
        </span>
      );
    } else if (xCount === 1) {
      return (
        <span>
          <FaExpand className="me-2 text-primary" />
          {description}
          <small className="text-muted ms-2">(2D)</small>
        </span>
      );
    } else {
      return (
        <span>
          <FaRuler className="me-2 text-secondary" />
          {description}
          <small className="text-muted ms-2">(1D)</small>
        </span>
      );
    }
  };

  // Filtrar medidas según el toggle
  const filteredMeasurements = showInactive 
    ? measurements 
    : measurements.filter(measurement => measurement.active);

  return (
    <div className="sku-measurements-manager">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <FaRuler className="me-2 text-primary" />
            Gestión de Medidas de SKU
          </h5>
          <div className="d-flex gap-2 align-items-center">
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="showInactiveToggle"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showInactiveToggle">
                Mostrar inactivos
              </label>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleNew}
              disabled={loading}
            >
              <FaPlus className="me-2" />
              Nueva Medida
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Filtro por SKU */}
          <div className="row mb-3">
            <div className="col-md-4">
              <label className="form-label">
                <FaFilter className="me-2" />
                Filtrar por SKU
              </label>
              <select
                className="form-select"
                value={filterSkuId}
                onChange={(e) => setFilterSkuId(e.target.value)}
                disabled={loading}
              >
                <option value="">Todos los SKUs</option>
                {skus.map(sku => (
                  <option key={sku.id} value={sku.id}>
                    {sku.sku_description || `SKU #${sku.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!showInactive && measurements.length > filteredMeasurements.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{measurements.length - filteredMeasurements.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando medidas...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>SKU</th>
                    <th>Medida</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {!Array.isArray(filteredMeasurements) || filteredMeasurements.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        {!Array.isArray(measurements) ? 
                          'Error cargando medidas' : 
                          filterSkuId ? (
                            showInactive ? 'No hay medidas para este SKU' : 'No hay medidas activas para este SKU'
                          ) : (
                            showInactive ? 'No hay medidas registradas' : 'No hay medidas activas. Activa el toggle "Mostrar inactivos" para ver medidas eliminadas.'
                          )
                        }
                      </td>
                    </tr>
                  ) : (
                    filteredMeasurements.map(measurement => (
                      <tr key={measurement.id}>
                        <td>#{measurement.id}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <FaCube className="me-2 text-success" />
                            <span className="fw-bold">{getSkuName(measurement.sku_id)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            {formatMeasureDescription(measurement.measure_description)}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${measurement.active ? 'bg-success' : 'bg-danger'}`}>
                            {measurement.active ? (
                              <>
                                <FaCheck className="me-1" />
                                Activo
                              </>
                            ) : (
                              <>
                                <FaTimes className="me-1" />
                                Inactivo
                              </>
                            )}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {measurement.inserted_date ? new Date(measurement.inserted_date).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(measurement)}
                            disabled={loading}
                            title="Editar medida"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(measurement)}
                            disabled={loading}
                            title="Eliminar medida"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar medida */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingMeasurement ? 'Editar Medida' : 'Nueva Medida'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">SKU *</label>
                        <select
                          className="form-select"
                          name="sku_id"
                          value={formData.sku_id}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                        >
                          <option value="">Selecciona un SKU</option>
                          {skus.map(sku => (
                            <option key={sku.id} value={sku.id}>
                              {sku.sku_description || `SKU #${sku.id}`}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Descripción de la Medida *</label>
                        <input
                          type="text"
                          className="form-control text-uppercase"
                          name="measure_description"
                          value={formData.measure_description}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          placeholder="Ej: 120X60X40, 90X120, 100"
                          style={{ textTransform: 'uppercase' }}
                        />
                        <div className="form-text">
                          Formato: AnchoxLargoxAlto (ej: 120X60X40) - Se convertirá a mayúsculas
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <label className="form-check-label">
                            <FaCheck className="me-2 text-success" />
                            Medida activa
                          </label>
                          <div className="form-text">
                            Las medidas inactivas no aparecerán en los selectores
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      {/* Medidas comunes */}
                      <div className="mb-3">
                        <label className="form-label">Medidas comunes:</label>
                        <div className="row">
                          {commonMeasurements.map(measure => (
                            <div key={measure} className="col-6 mb-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary w-100"
                                onClick={() => setFormData(prev => ({ ...prev, measure_description: measure }))}
                                disabled={loading}
                              >
                                <FaRuler className="me-1" />
                                {measure}
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="alert alert-info">
                    <FaRuler className="me-2" />
                    <strong>Nota:</strong> Las medidas precargadas aparecerán como opciones en los selectores al crear productos. Los usuarios también podrán crear medidas personalizadas durante la carga.
                  </div>

                  {/* Ejemplos visuales */}
                  <div className="row">
                    <div className="col-md-4">
                      <div className="card border-primary">
                        <div className="card-body text-center">
                          <FaRuler className="display-6 text-secondary mb-2" />
                          <h6>1D - Medida simple</h6>
                          <small className="text-muted">Ej: 100, 120</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-primary">
                        <div className="card-body text-center">
                          <FaExpand className="display-6 text-primary mb-2" />
                          <h6>2D - Ancho x Largo</h6>
                          <small className="text-muted">Ej: 90X120, 200X130</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card border-info">
                        <div className="card-body text-center">
                          <FaCube className="display-6 text-info mb-2" />
                          <h6>3D - Ancho x Largo x Alto</h6>
                          <small className="text-muted">Ej: 100X190X26</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.sku_id || !formData.measure_description.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingMeasurement ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar la medida <strong>"{deletingMeasurement?.measure_description}"</strong> del SKU <strong>{getSkuName(deletingMeasurement?.sku_id)}</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  Esta acción eliminará permanentemente la medida del sistema.
                </div>
                <div className="alert alert-info">
                  <FaRuler className="me-2" />
                  Los productos que ya usen esta medida no se verán afectados.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkuMeasurementsManager;
